import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ProductCard } from "./types";
import { productCards } from "./data";
import {
  animateHeading,
  animateCards,
  addCardHoverEffects,
  fadeInImage,
  cleanupScrollTriggers,
} from "./animation";

const FeaturedProducts: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const imagesRef = useRef<(HTMLImageElement | null)[]>([]);
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>([]);

  // Initialize imagesLoaded array
  useEffect(() => {
    setImagesLoaded(new Array(productCards.length).fill(false));
  }, []);

  useEffect(() => {
    // Wait a small delay for DOM to be fully ready
    const animationTimeout = setTimeout(() => {
      // Apply heading animation
      animateHeading({ current: headingRef.current });

      // Apply card animations
      animateCards(
        { current: sectionRef.current },
        { current: cardsRef.current }
      );

      // Add hover effects to cards
      const cleanupHoverEffects = addCardHoverEffects({
        current: cardsRef.current,
      });

      // Return cleanup function
      return () => {
        clearTimeout(animationTimeout);
        cleanupHoverEffects();
        cleanupScrollTriggers();
      };
    }, 100);

    // Clean up timeout if component unmounts before timeout completes
    return () => clearTimeout(animationTimeout);
  }, []);

  // Handle image loading
  const handleImageLoad = (index: number) => {
    if (imagesRef.current[index]) {
      // Update loaded state
      setImagesLoaded((prev) => {
        const newState = [...prev];
        newState[index] = true;
        return newState;
      });

      // Apply fade-in animation
      fadeInImage(imagesRef.current[index] as HTMLImageElement, index);
    }
  };

  // Check if file is a GIF
  const isGif = (path: string): boolean => {
    return path.toLowerCase().endsWith(".gif");
  };

  return (
    <section ref={sectionRef} className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <h2
          ref={headingRef}
          className="text-3xl md:text-4xl font-bold text-center mb-12"
        >
          Featured Products
        </h2>

        {/* Updated grid layout: 1 column on mobile, 2 columns on medium screens, 4 columns on large screens */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {productCards.map((product: ProductCard, index: number) => (
            <div
              key={product.id}
              ref={(el) => (cardsRef.current[index] = el)}
              className="bg-gray-50 rounded-lg overflow-hidden shadow-md"
            >
              <div className="h-48 bg-gray-200 overflow-hidden relative">
                {product.image && (
                  <>
                    {/* Loading placeholder */}
                    {!imagesLoaded[index] && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                        <div className="w-8 h-8 border-4 border-cannabis-green-300 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}

                    {/* Regular image for GIFs */}
                    {isGif(product.image) ? (
                      <img
                        src={product.image}
                        alt={product.title}
                        ref={(el) => (imagesRef.current[index] = el)}
                        onLoad={() => handleImageLoad(index)}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                        style={{ opacity: 0 }} // Start with opacity 0
                      />
                    ) : (
                      /* Picture element with WebP support for non-GIFs */
                      <picture>
                        {product.imageWebp && (
                          <source
                            srcSet={product.imageWebp}
                            type="image/webp"
                          />
                        )}
                        <source
                          srcSet={product.image}
                          type={`image/${product.image.split(".").pop()}`}
                        />
                        <img
                          src={product.image}
                          width="600"
                          height="400"
                          alt={product.title}
                          ref={(el) => (imagesRef.current[index] = el)}
                          onLoad={() => handleImageLoad(index)}
                          onError={() => handleImageLoad(index)} // Add error handler
                          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                          style={{ opacity: 0 }} // Start with opacity 0
                        />
                      </picture>
                    )}
                  </>
                )}
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">{product.title}</h3>
                <p className="text-gray-600 mb-4">{product.description}</p>
                <Link
                  to={product.link}
                  className="text-cannabis-green-500 font-medium hover:underline inline-flex items-center"
                >
                  {product.linkText}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
